import { useRef, useState } from 'react';
import { FileText, Box, Loader2, CheckCircle2, XCircle, MinusCircle, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useOnshapeContext } from '@/hooks/useOnshapeContext';
import { preserveQueryParams } from '@/lib/navigation-utils';
import { analyzeHardwareDatasheet, startHardwareImport, finishHardwareImport } from '@/lib/onshape-api';

const MAX_FILE_MB = 15;
// SOLIDWORKS translations took ~3.5 min in testing; give Onshape plenty of room.
// Every poll is a billable Onshape API call, so don't poll faster than this.
const IMPORT_POLL_MS = 10000;
const IMPORT_TIMEOUT_MS = 10 * 60 * 1000;

const DEFAULT_COLOR = '#c0c4c8';

// "#AbC", "abc", "aabbcc" → "#aabbcc"; null if it isn't a hex color. Mirrors the backend's normalizeHexColor.
function normalizeHex(value) {
  let h = String(value || '').trim().replace(/^#/, '').toLowerCase();
  if (/^[0-9a-f]{3}$/.test(h)) h = h.split('').map((c) => c + c).join('');
  return /^[0-9a-f]{6}$/.test(h) ? `#${h}` : null;
}

const RENAME_PREF_KEY = 'otto_hardware_rename_document';

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Short vendor names for document titles, keyed by the vendor lowercased with
// non-alphanumerics stripped. Hardcoded company defaults for now; would become
// a per-user setting if Otto is published to the Onshape App Store.
const DOCUMENT_VENDOR_NAMES = {
  mcmastercarr: 'McMaster',
  mcmaster: 'McMaster',
};

const documentVendorName = (vendor) => {
  const v = (vendor || '').trim();
  return DOCUMENT_VENDOR_NAMES[v.toLowerCase().replace(/[^a-z0-9]/g, '')] || v;
};

// Company standard document name: [Vendor] [Part Number] [Description].
const defaultDocumentName = (f) =>
  [documentVendorName(f.vendor), f.partNumber, f.description]
    .map((v) => (v || '').trim()).filter(Boolean).join(' ');

function readRenamePref() {
  try {
    return localStorage.getItem(RENAME_PREF_KEY) === 'true';
  } catch {
    return false;
  }
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function FilePicker({ label, hint, accept, icon: Icon, file, onChange }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const accepts = (f) => {
    const ext = f.name.toLowerCase().slice(f.name.lastIndexOf('.'));
    return accept.split(',').includes(ext);
  };

  return (
    <div
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const dropped = e.dataTransfer.files?.[0];
        if (dropped && accepts(dropped)) onChange(dropped);
      }}
      className={`flex cursor-pointer items-center gap-3 rounded-lg border border-dashed p-3 transition-colors ${
        dragOver ? 'border-primary bg-primary/5' : 'border-input hover:bg-muted/50'
      }`}
    >
      <Icon className="h-5 w-5 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{label}</p>
        <p className="truncate text-xs text-muted-foreground">{file ? file.name : hint}</p>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => e.target.files?.[0] && onChange(e.target.files[0])}
      />
    </div>
  );
}

function Field({ label, children, note }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
      {note && <span className="block text-[11px] text-muted-foreground">{note}</span>}
    </label>
  );
}

function StepStatus({ label, result }) {
  if (!result) return null;
  const Icon = result.ok ? CheckCircle2 : XCircle;
  return (
    <li className="flex items-start gap-2 text-xs">
      <Icon className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${result.ok ? 'text-green-600' : 'text-destructive'}`} />
      <span>
        {label}
        {!result.ok && <span className="block break-words text-muted-foreground">{result.error}</span>}
      </span>
    </li>
  );
}

export function HardwareImport() {
  const { documentId, workspaceId, access, isValid } = useOnshapeContext();

  const [cadFile, setCadFile] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [fields, setFields] = useState(null);
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('idle'); // idle | analyzing | review | importing | done
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [materialOptions, setMaterialOptions] = useState([]);
  const [materialColors, setMaterialColors] = useState([]);
  const [importElapsed, setImportElapsed] = useState(0);
  const [renameDocument, setRenameDocument] = useState(readRenamePref);
  // null = follow the fields (vendor / part number / description); a string once the user edits it.
  const [documentNameEdit, setDocumentNameEdit] = useState(null);

  const setField = (key, value) => setFields((f) => ({ ...f, [key]: value }));
  // Picking a material with a saved color (Settings → Material colors) applies that color.
  const setMaterial = (material) => {
    const key = material.trim().toLowerCase();
    const saved = materialColors.find((r) => r.material.trim().toLowerCase() === key);
    setFields((f) => ({ ...f, material, ...(saved ? { color: saved.color } : {}) }));
  };
  const documentName = documentNameEdit ?? (fields ? defaultDocumentName(fields) : '');
  const toggleRename = (on) => {
    setRenameDocument(on);
    try {
      localStorage.setItem(RENAME_PREF_KEY, String(on));
    } catch {
      // Preference just won't persist.
    }
  };

  const checkSize = (file) => {
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      throw new Error(`${file.name} is larger than ${MAX_FILE_MB} MB`);
    }
  };

  const handleAnalyze = async () => {
    setError('');
    setStatus('analyzing');
    try {
      checkSize(pdfFile);
      const pdfBase64 = await readFileAsBase64(pdfFile);
      const { fields: extracted, materialOptions: options, materialColors: colors } =
        await analyzeHardwareDatasheet(pdfBase64, cadFile?.name);
      setMaterialOptions(options || []);
      setMaterialColors(colors || []);
      setFields(extracted);
      setDocumentNameEdit(null);
      setNotes(extracted.notes);
      setStatus('review');
    } catch (err) {
      setError(err.message);
      setStatus('idle');
    }
  };

  const handleImport = async () => {
    setError('');
    setImportElapsed(0);
    setStatus('importing');
    try {
      checkSize(cadFile);
      const [cadBase64, pdfBase64] = await Promise.all([
        readFileAsBase64(cadFile),
        pdfFile ? readFileAsBase64(pdfFile) : Promise.resolve(null),
      ]);
      const confirmedFields = fields;
      const started = await startHardwareImport({
        documentId,
        workspaceId,
        cadBase64,
        cadFilename: cadFile.name,
        pdfBase64,
        pdfFilename: pdfFile?.name,
        fields: confirmedFields,
        documentName: renameDocument ? documentName.trim() : undefined,
      });

      // Onshape translates asynchronously; poll until the Part Studio exists and fields are applied.
      const startedAt = Date.now();
      let res = { state: 'ACTIVE' };
      while (res.state === 'ACTIVE') {
        if (Date.now() - startedAt > IMPORT_TIMEOUT_MS) {
          throw new Error('Onshape is still converting the file. It may finish on its own; check the document tabs.');
        }
        await sleep(IMPORT_POLL_MS);
        setImportElapsed(Math.round((Date.now() - startedAt) / 1000));
        res = await finishHardwareImport({
          documentId, workspaceId, translationId: started.translationId, fields: confirmedFields,
        });
      }
      res.warnings = [...(started.warnings || []), ...(res.warnings || [])];
      res.pdfElementId = started.pdfElementId;
      res.documentName = started.documentName;
      setResult(res);
      setStatus('done');
    } catch (err) {
      setError(err.message);
      setStatus('review');
    }
  };

  const reset = () => {
    setCadFile(null);
    setPdfFile(null);
    setFields(null);
    setNotes('');
    setDocumentNameEdit(null);
    setResult(null);
    setError('');
    setStatus('idle');
  };

  const partStudioUrl = result?.partStudioElementId
    ? `https://cad.onshape.com/documents/${documentId}/w/${workspaceId}/e/${result.partStudioElementId}`
    : null;

  return (
    <div className="mx-auto max-w-xl space-y-5 p-4">
      <div>
        <h1 className="text-lg font-semibold">Add hardware</h1>
        <p className="text-sm text-muted-foreground">
          Turn a vendor part into a named, attributed part in this document.
        </p>
      </div>

      {status === 'idle' && (
        <div className="space-y-3 rounded-lg bg-muted/50 p-3">
          <ol className="space-y-2 text-sm">
            {[
              ['Download the files', 'Get the CAD (STEP or SolidWorks) and the datasheet PDF from the vendor, e.g. McMaster-Carr.'],
              ['Drop them in below', 'Click Read datasheet. Otto fills in the name, part number, vendor, material and color.'],
              ['Check and add', 'Fix anything that looks off, then click Add to document.'],
            ].map(([title, body], i) => (
              <li key={title} className="flex gap-2">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-semibold text-primary-foreground">
                  {i + 1}
                </span>
                <span>
                  <span className="font-medium">{title}.</span>{' '}
                  <span className="text-muted-foreground">{body}</span>
                </span>
              </li>
            ))}
          </ol>
          <p className="text-xs text-muted-foreground">
            You'll get a new Part Studio tab with the part, plus the datasheet as its own tab. Nothing is added to
            the document until you click Add to document.
          </p>
          <p className="text-xs text-muted-foreground">
            Set a default color for each material in{' '}
            <Link to={preserveQueryParams('/settings')} className="text-primary underline">Settings</Link>.
          </p>
        </div>
      )}

      {!isValid && (
        <p className="rounded-md bg-muted p-3 text-sm">Open Otto from an Onshape document to import hardware.</p>
      )}
      {isValid && access === 'read' && (
        <p className="rounded-md bg-muted p-3 text-sm">This document is read-only. Switch to a workspace you can edit.</p>
      )}

      {status !== 'done' && (
        <div className="space-y-2">
          <FilePicker
            label="CAD file"
            hint="STEP, SolidWorks, Parasolid…"
            accept=".step,.stp,.x_t,.x_b,.igs,.iges,.sldprt"
            icon={Box}
            file={cadFile}
            onChange={setCadFile}
          />
          <FilePicker
            label="Datasheet"
            hint="PDF from the vendor"
            accept=".pdf"
            icon={FileText}
            file={pdfFile}
            onChange={setPdfFile}
          />
          {status === 'idle' || status === 'analyzing' ? (
            <Button className="w-full" disabled={!pdfFile || status === 'analyzing'} onClick={handleAnalyze}>
              {status === 'analyzing' ? <><Loader2 className="animate-spin" /> Reading datasheet…</> : 'Read datasheet'}
            </Button>
          ) : null}
        </div>
      )}

      {error && <p className="rounded-md border border-destructive/50 p-3 text-sm text-destructive">{error}</p>}

      {fields && (status === 'review' || status === 'importing') && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold">Check before importing</h2>
          {notes && <p className="rounded-md bg-muted p-2 text-xs">{notes}</p>}

          <Field label="Name">
            <Input value={fields.name} onChange={(e) => setField('name', e.target.value)} />
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Part number">
              <Input value={fields.partNumber} onChange={(e) => setField('partNumber', e.target.value)} />
            </Field>
            <Field label="Vendor">
              <Input value={fields.vendor} onChange={(e) => setField('vendor', e.target.value)} />
            </Field>
          </div>
          <Field label="Description">
            <Input value={fields.description} onChange={(e) => setField('description', e.target.value)} />
          </Field>
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <Field
              label="Material"
              note={fields.material && materialOptions.length && !materialOptions.some(
                (m) => m.toLowerCase() === fields.material.trim().toLowerCase())
                ? 'Not in the Onshape Material Library; material will be skipped'
                : 'From the Onshape Material Library'}
            >
              <Input
                list="otto-material-options"
                value={fields.material}
                onChange={(e) => setMaterial(e.target.value)}
              />
              <datalist id="otto-material-options">
                {materialOptions.map((m) => <option key={m} value={m} />)}
              </datalist>
            </Field>
            <Field
              label="Color"
              note={fields.color && !normalizeHex(fields.color) ? 'Not a hex color; appearance will be skipped' : null}
            >
              <div className="flex gap-1">
                <input
                  type="color"
                  value={normalizeHex(fields.color) || DEFAULT_COLOR}
                  onChange={(e) => setField('color', e.target.value)}
                  className="h-9 w-12 shrink-0 cursor-pointer rounded-md border border-input bg-transparent p-1"
                />
                <Input
                  value={fields.color}
                  placeholder={DEFAULT_COLOR}
                  spellCheck={false}
                  className="w-24 font-mono"
                  onChange={(e) => setField('color', e.target.value.trim())}
                  onBlur={() => normalizeHex(fields.color) && setField('color', normalizeHex(fields.color))}
                />
              </div>
            </Field>
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={renameDocument}
                onChange={(e) => toggleRename(e.target.checked)}
                className="h-4 w-4 cursor-pointer"
              />
              Rename this document
            </label>
            {renameDocument && (
              <Field label="Document name" note="Vendor, part number, description">
                <Input value={documentName} onChange={(e) => setDocumentNameEdit(e.target.value)} />
              </Field>
            )}
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={reset} disabled={status === 'importing'}>Start over</Button>
            <Button
              className="flex-1"
              disabled={!cadFile || !isValid || access === 'read' || status === 'importing'}
              onClick={handleImport}
            >
              {status === 'importing'
                ? <><Loader2 className="animate-spin" /> {importElapsed < 5
                    ? 'Uploading…'
                    : `Onshape is converting… ${Math.floor(importElapsed / 60)}:${String(importElapsed % 60).padStart(2, '0')}`}</>
                : cadFile ? 'Add to document' : 'Choose a CAD file first'}
            </Button>
          </div>
        </div>
      )}

      {status === 'done' && result && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            <h2 className="text-sm font-semibold">Imported</h2>
          </div>
          {partStudioUrl && (
            <a href={partStudioUrl} target="_top" className="inline-flex items-center gap-1 text-sm text-primary underline">
              Open the new Part Studio <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
          <ul className="space-y-1">
            <StepStatus label="Datasheet saved as a tab" result={pdfFile ? { ok: !!result.pdfElementId, error: 'See warnings' } : null} />
            <StepStatus
              label={`Document renamed to "${result.documentName || documentName}"`}
              result={renameDocument && documentName.trim() ? { ok: !!result.documentName, error: 'See warnings' } : null}
            />
            <StepStatus
              label={`Import feature renamed to "${result.importFeatureName || fields?.partNumber || ''}"`}
              result={fields?.partNumber?.trim() ? { ok: !!result.importFeatureName, error: 'See warnings' } : null}
            />
          </ul>
          {result.parts.map((part) => (
            <div key={part.partId} className="rounded-md border p-3">
              <p className="mb-2 text-xs font-medium">{part.originalName || part.partId}</p>
              {part.error ? (
                <StepStatus label="Update part" result={{ ok: false, error: part.error }} />
              ) : (
                <ul className="space-y-1">
                  <StepStatus label="Name, description, part number, vendor" result={part.results.properties} />
                  <StepStatus label="Material" result={part.results.material} />
                  <StepStatus label="Appearance" result={part.results.appearance} />
                  {part.results.skipped.map((s) => (
                    <li key={s.field} className="flex items-start gap-2 text-xs text-muted-foreground">
                      <MinusCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span>Skipped: {s.reason}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
          {result.warnings.map((w) => (
            <p key={w} className="rounded-md bg-muted p-2 text-xs">{w}</p>
          ))}
          <Button variant="outline" className="w-full" onClick={reset}>Add another</Button>
        </div>
      )}
    </div>
  );
}
